import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ClipboardList, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useInspecciones, useUsuarios } from '@/datos/consultas';
import {
  busquedaInspecciones,
  desdeRango,
  filtrar,
  ordenarPorFecha,
  RANGO_POR_DEFECTO,
  type FiltrosInspecciones,
  type Rango,
} from '@/inspecciones/filtros';
import { ListaInspeccionesMovil, TablaInspecciones } from '@/inspecciones/TablaInspecciones';
import { useMedio } from '@/lib/medios';
import { inicioDelDia, useAhora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto, Segmentado, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';

export const Route = createFileRoute('/_app/inspecciones/')({
  validateSearch: busquedaInspecciones,
  component: Inspecciones,
});

const opcionesRango: { valor: Rango; texto: string }[] = [
  { valor: 'hoy', texto: 'Hoy' },
  { valor: '7', texto: '7 días' },
  { valor: '30', texto: '30 días' },
  { valor: '90', texto: '90 días' },
  { valor: 'todo', texto: 'Todo' },
];

function Select<T extends string>({
  etiqueta,
  valor,
  opciones,
  onCambio,
}: {
  etiqueta: string;
  valor: T | undefined;
  opciones: { valor: T; texto: string }[];
  onCambio: (v: T | undefined) => void;
}) {
  return (
    <label className="relative flex items-center">
      <span className="sr-only">{etiqueta}</span>
      <select
        value={valor ?? ''}
        onChange={(e) => onCambio((e.target.value || undefined) as T | undefined)}
        className={cn(
          'h-[38px] appearance-none rounded-[10px] border bg-surface pr-8 pl-3 text-[14px] transition-colors',
          valor ? 'border-primary font-bold text-primary-text' : 'border-border-input text-text',
          'focus:border-primary focus:shadow-[0_0_0_1px_var(--tm-primary),0_0_0_4px_var(--tm-primary-soft)] focus:outline-none',
        )}
      >
        <option value="">{etiqueta}: todos</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-2.5 size-4 text-text-2"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </label>
  );
}

function Inspecciones() {
  const filtros = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const ahora = useAhora(5 * 60_000);
  const desde = desdeRango(filtros.rango, inicioDelDia(ahora));
  const consulta = useInspecciones(desde);
  const usuariosQ = useUsuarios();
  const movil = !useMedio('(min-width: 768px)');
  const [patente, setPatente] = useState(filtros.patente ?? '');
  const escritorio = useMedio('(min-width: 1280px)');

  const usuarios = useMemo(
    () => new Map((usuariosQ.data ?? []).map((u) => [u.id, u])),
    [usuariosQ.data],
  );
  const filas = useMemo(
    () => ordenarPorFecha(filtrar(consulta.data ?? [], filtros)),
    [consulta.data, filtros],
  );

  const fijar = (cambios: Partial<FiltrosInspecciones>) =>
    void navegar({ search: (p) => ({ ...p, ...cambios }), replace: true });

  const periodo = (
    <Segmentado
      etiqueta="Período"
      tamano="sm"
      valor={filtros.rango ?? RANGO_POR_DEFECTO}
      onCambio={(v) => fijar({ rango: v === RANGO_POR_DEFECTO ? undefined : v })}
      opciones={opcionesRango}
    />
  );

  const hayFiltros =
    !!filtros.tipo ||
    !!filtros.estado ||
    !!filtros.revision ||
    !!filtros.patente ||
    !!filtros.nuevo;
  const total = consulta.data?.length ?? 0;
  const rangoTexto = opcionesRango.find(
    (o) => o.valor === (filtros.rango ?? RANGO_POR_DEFECTO),
  )?.texto;

  return (
    <div className="flex h-[calc(100dvh-78px-env(safe-area-inset-bottom))] flex-col lg:h-dvh">
      <Encabezado
        titulo="Inspecciones"
        ayuda="inspecciones"
        acciones={escritorio ? periodo : undefined}
        subtitulo={
          consulta.data
            ? hayFiltros
              ? `${filas.length} de ${total} · ${rangoTexto?.toLowerCase()}`
              : `${total} ${total === 1 ? 'inspección' : 'inspecciones'} · ${rangoTexto === 'Todo' ? 'todas' : rangoTexto === 'Hoy' ? 'hoy' : `últimos ${rangoTexto}`}`
            : 'Cargando…'
        }
      />
      <div className="sin-scrollbar flex flex-none items-center gap-2.5 overflow-x-auto border-b border-border bg-surface px-4 py-3 *:flex-none sm:flex-wrap sm:px-7">
        <Segmentado
          etiqueta="Tipo de inspección"
          valor={filtros.tipo ?? 'todas'}
          onCambio={(v) => fijar({ tipo: v === 'todas' ? undefined : v })}
          opciones={[
            { valor: 'todas', texto: 'Todas' },
            { valor: 'siniestro', texto: 'Siniestro' },
            { valor: 'recepcion', texto: 'Ingreso' },
            { valor: 'reingreso', texto: 'Reingreso' },
            { valor: 'entrega', texto: 'Entrega' },
          ]}
        />
        <button
          type="button"
          aria-pressed={!!filtros.nuevo}
          onClick={() => fijar({ nuevo: filtros.nuevo ? undefined : true })}
          className={cn(
            'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border px-3 text-[14px] transition-colors',
            filtros.nuevo
              ? 'border-que-strong bg-que-soft font-bold text-que-texto'
              : 'border-border-input bg-surface text-text hover:bg-surface-muted',
          )}
        >
          <span aria-hidden="true">✕</span> Con daño nuevo
        </button>
        <Select
          etiqueta="Estado"
          valor={filtros.estado}
          onCambio={(v) => fijar({ estado: v })}
          opciones={[
            { valor: 'firmada', texto: 'Firmadas' },
            { valor: 'borrador', texto: 'Borradores' },
            { valor: 'anulada', texto: 'Anuladas' },
          ]}
        />
        <Select
          etiqueta="Revisión"
          valor={filtros.revision}
          onCambio={(v) => fijar({ revision: v })}
          opciones={[
            { valor: 'pendiente', texto: 'Sin revisar' },
            { valor: 'aprobada', texto: 'Aprobadas' },
            { valor: 'observada', texto: 'Observadas' },
          ]}
        />
        <form
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            fijar({ patente: patente.trim() || undefined });
          }}
          role="search"
        >
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-2" />
          <input
            value={patente}
            onChange={(e) => {
              setPatente(e.target.value);
              // Live filter: cheap (client-side over the loaded range).
              fijar({ patente: e.target.value.trim() || undefined });
            }}
            placeholder="Patente"
            aria-label="Filtrar por patente"
            className="h-[38px] w-[150px] rounded-[10px] border border-border-input bg-surface pr-8 pl-9 text-[14px] uppercase placeholder:normal-case focus:border-primary focus:shadow-[0_0_0_1px_var(--tm-primary),0_0_0_4px_var(--tm-primary-soft)] focus:outline-none"
          />
          {patente && (
            <button
              type="button"
              onClick={() => {
                setPatente('');
                fijar({ patente: undefined });
              }}
              className="absolute top-1/2 right-1.5 grid size-7 -translate-y-1/2 place-items-center rounded-[7px] text-text-2 hover:bg-surface-muted"
              aria-label="Limpiar patente"
            >
              <X className="size-3.5" />
            </button>
          )}
        </form>
        {!escritorio && periodo}
      </div>

      {consulta.isError ? (
        <div className="p-6">
          <ErrorCarga error={consulta.error} />
        </div>
      ) : !consulta.data ? (
        <div className="flex flex-col gap-0 px-7 pt-4" aria-busy="true">
          {Array.from({ length: 10 }, (_, i) => (
            <Esqueleto
              key={i}
              className="my-2 h-9 rounded-[8px]"
              style={{ opacity: 1 - i * 0.08 }}
            />
          ))}
        </div>
      ) : filas.length === 0 ? (
        <Vacio
          icono={<ClipboardList />}
          titulo={
            hayFiltros
              ? 'Ninguna inspección coincide con los filtros'
              : 'Aún no hay inspecciones en este período'
          }
          accion={
            hayFiltros ? (
              <Boton
                onClick={() => {
                  setPatente('');
                  void navegar({ search: { rango: filtros.rango }, replace: true });
                }}
              >
                Quitar filtros
              </Boton>
            ) : filtros.rango !== 'todo' ? (
              <Boton onClick={() => fijar({ rango: 'todo' })}>Ver todas</Boton>
            ) : undefined
          }
        >
          {hayFiltros
            ? 'Prueba con otro período o quita alguno de los filtros.'
            : 'Los siniestros, ingresos y entregas cerrados en la tablet aparecen aquí al instante.'}
        </Vacio>
      ) : movil ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ListaInspeccionesMovil filas={filas} usuarios={usuarios} />
        </div>
      ) : (
        <TablaInspecciones filas={filas} usuarios={usuarios} />
      )}
    </div>
  );
}
